import { useEffect, useRef, useState } from "react";

import { GetTaskBoardResponse } from "../../service/query";
import { getTaskBoard } from "../../service/query/QueryService.full";
import { Status } from "../../service/Shared";

type TaskPanelWorkflow = GetTaskBoardResponse["results"][number];

/**
 * Loads the task board (workflow) that a selected task belongs to.
 *
 * The panel derives its steps and the current user's organisation access level from this
 * object, so it must never be left holding the previously selected task's workflow: the
 * board is dropped the moment the selection changes, and a response that arrives after the
 * selection has moved on is discarded.
 *
 * A general task has no workflow (null uuid), so nothing is fetched and undefined is returned.
 */
export const useTaskPanelWorkflow = (
  workflowUuid: string | null | undefined,
  onError?: () => void,
  refreshKey?: unknown
): TaskPanelWorkflow | undefined => {
  const [workflow, setWorkflow] = useState<TaskPanelWorkflow>();

  // Kept in a ref so an inline callback doesn't re-trigger the fetch on every render.
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  });

  useEffect(() => {
    // Drop the previous board before the new request starts, so the panel can't render one
    // workflow's steps or access level against another workflow's task while it loads.
    setWorkflow(undefined);

    // Flipped by the cleanup below; a response for a superseded selection is ignored.
    let isCurrent = true;

    const loadWorkflow = async (): Promise<void> => {
      try {
        const res = await getTaskBoard({
          showComplete: true,
          paging: {
            afterCursor: null,
            beforeCursor: null,
            limit: 1,
          },
          filter: {
            results: {
              uuid: {
                operator: "eq",
                value: workflowUuid as string,
              },
            },
          },
        });

        if (!isCurrent) return;

        if (res.status === Status.Success && res.data) {
          setWorkflow(res.data.results[0]);
        } else {
          onErrorRef.current?.();
        }
      } catch {
        if (isCurrent) onErrorRef.current?.();
      }
    };

    if (workflowUuid) {
      loadWorkflow();
    }

    return () => {
      isCurrent = false;
    };
  }, [workflowUuid, refreshKey]);

  return workflow;
};
